/* The right-hand sidebar: the gang sheets, the selected design's controls
   (size, rotate, duplicate, align, delete, resolution) or, with nothing
   selected, the designs on this sheet and their quantities. The live price
   and Review & order stay pinned at the bottom. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney, formatDims, formatMetres, effectiveDpi, itemBox, sheetStats, requestedCount, isManualDoc, type Item, type Design } from '@gsb/core';
import {
  useStore, build, setActiveSheet, addSheet, removeSheet, align, distribute,
  updateItems, clampInside, fillWith, addCopy, toast, select, setQuantity, autoSize, openPanel, setSideHidden, setLockAspect, aspectOf, stretchText
} from '../store/store';
import { SheetThumb } from '../canvas/SheetThumb';
import { Icon } from '../ui/icons';
import { NumberField, Stepper, Dialog } from '../ui/primitives';
import { ChecksSummary, useEstimate, usePrice, usePreflight, canOrder } from '../ui/Panels';
import { DpiBadge } from '../ui/quality';
import { textSpecOf } from '../text/textSpec';
import { resizeSelection, moveSelection, rotateSelection, flipSelection, toggleLockPosition, resetSelection, resetState, layerSelection, duplicateSelection, deleteSelection, isTurned } from './selectionCommands';

export function Sidebar({ onReview }: { onReview: () => void }) {
  const sel = useStore(s => s.selection);
  const doc = useStore(s => s.doc);
  const items = useMemo(() => doc.sheets.flatMap(s => s.items).filter(i => sel.includes(i.id)), [doc.sheets, sel]);
  return (
    <div className="side">
      <div className="side-h no-phone">
        <span className="side-t">Your gangsheet</span>
        <button className="ibtn ibtn-sm" aria-label="Hide the sheet panel" data-tip="Hide this panel (more room for the sheet)" data-tip-pos="left" onClick={() => setSideHidden(true)}><Icon name="panel" /></button>
      </div>
      <div className="side-b">
        {doc.sheets.length > 0 && <SheetList />}
        {items.length ? <SelectionProps key={items.length === 1 ? items[0].id : 'multi'} items={items} /> : doc.sheets.length ? <OnSheet onReview={onReview} /> : <BeforeBuild />}
      </div>
      <SideFoot onReview={onReview} />
    </div>
  );
}

/* ------------------------------------------- nothing built yet: a summary
   (the designs themselves are in the Designs panel; not repeated here) */
function BeforeBuild() {
  const doc = useStore(s => s.doc);
  const assets = useStore(s => s.assets);
  const est = useEstimate();
  const n = doc.designs.length;
  const transfers = requestedCount(doc);
  const waiting = doc.designs.filter(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing').length;
  if (!n) {
    return (
      <section className="sbox sbox-quiet">
        <p className="small muted">Your sheets, designs and price show up here as you go.</p>
        <p className="small muted">Start by uploading your artwork, or add some text.</p>
      </section>
    );
  }
  return (
    <section className="sbox">
      <div className="sbox-h"><span className="sbox-t">Ready to build</span></div>
      <dl className="sumlist">
        <div><dt>Designs</dt><dd><b className="num">{n}</b>{waiting > 0 && <span className="muted"> · {waiting} uploading</span>}<button className="link small" onClick={() => openPanel('designs')}>Sizes &amp; quantities</button></dd></div>
        <div><dt>Transfers</dt><dd><b className="num">{transfers}</b></dd></div>
        {est?.lengthMm ? <div><dt>Film</dt><dd>about <b className="num">{formatMetres(est.lengthMm)}</b></dd></div> : null}
      </dl>
    </section>
  );
}

/* --------------------------------------------------------------- sheets */
function SheetList() {
  const sheets = useStore(s => s.doc.sheets);
  const active = useStore(s => s.activeSheet);
  const unit = useStore(s => s.doc.settings.unit);
  const fixed = useStore(s => s.config?.pricing.transfers[s.doc.transfer].fixedSheets);
  const [open, setOpen] = useState(true);
  const u = unit === 'mm' ? 'cm' : unit;
  return (
    <section className="sbox">
      <div className="sbox-h">
        <button className="sbox-t" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          <Icon name="chevDown" className={'chev' + (open ? '' : ' is-shut')} />Gang sheets <span className="count num">{sheets.length}</span>
        </button>
        <button className="ibtn ibtn-sm" onClick={addSheet} aria-label="Add a sheet" data-tip="Add a blank sheet" data-tip-pos="left"><Icon name="plus" /></button>
      </div>
      {open && (sheets.length ? (
        <ul className="sheets" role="tablist" aria-label="Sheets">
          {sheets.map((s, i) => {
            const st = sheetStats(s);
            const kind = s.kind === 'fixed' ? (fixed?.find(f => f.key === s.fixedKey)?.name || 'Fixed sheet') : s.kind === 'roll' ? 'By the metre' : 'Set length';
            return (
              <li key={s.id} className={'sh' + (i === active ? ' is-on' : '')}>
                <button role="tab" aria-selected={i === active} className="sh-main" onClick={() => setActiveSheet(i)}>
                  <span className="sh-th"><SheetThumb sheet={s} width={38} maxHeight={56} /></span>
                  <span className="sh-txt">
                    <b>Sheet {i + 1}</b>
                    <span className="num">{formatDims(s.widthMm, s.lengthMm, u)}</span>
                    <small>{kind} · {s.items.length} transfer{s.items.length === 1 ? '' : 's'} · {Math.round(st.utilisation * 100)}%</small>
                  </span>
                </button>
                {sheets.length > 1 && <button className="ibtn ibtn-sm sh-x" aria-label={'Remove sheet ' + (i + 1)} data-tip="Remove sheet" data-tip-pos="left" onClick={() => removeSheet(i)}><Icon name="trash" /></button>}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="small muted sbox-note">No sheets yet. Build your gangsheet and we’ll lay everything out for you.</p>
      ))}
    </section>
  );
}

/* --------------------------------------------- nothing selected: this sheet */
function OnSheet({ onReview }: { onReview: () => void }) {
  const doc = useStore(s => s.doc);
  const active = useStore(s => s.activeSheet);
  const assets = useStore(s => s.assets);
  const cfg = useStore(s => s.config)!;
  const unit = doc.settings.unit;
  const sheet = doc.sheets[active];
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    sheet?.items.forEach(i => m.set(i.designId, (m.get(i.designId) || 0) + 1));
    return m;
  }, [sheet]);
  const all = useMemo(() => {
    const m = new Map<string, number>();
    doc.sheets.forEach(s => s.items.forEach(i => m.set(i.designId, (m.get(i.designId) || 0) + 1)));
    return m;
  }, [doc.sheets]);
  const list = doc.designs.filter(d => counts.has(d.id));
  const pick = (d: Design) => {
    if (!sheet) return;
    select(sheet.items.filter(i => i.designId === d.id).map(i => i.id));
  };
  return (
    <>
      <section className="sbox">
        <div className="sbox-h"><span className="sbox-t">On this sheet <span className="count num">{list.length}</span></span></div>
        <p className="tiny muted sbox-note">Click a design on the sheet to size, rotate, duplicate or align it.</p>
        <ul className="onsheet">
          {list.map(d => {
            const a = assets[d.assetId];
            const placedAll = all.get(d.id) || 0;
            const short = placedAll < d.quantity;
            return (
              <li key={d.id} className="os">
                <button className="os-main" onClick={() => pick(d)} disabled={!sheet} aria-label={'Select ' + d.name + ' on the sheet'}>
                  <span className="thumb">{a?.thumbUrl ? <img src={a.thumbUrl} alt="" draggable={false} /> : <span className="spin" />}</span>
                  <span className="os-txt">
                    <b title={d.name}>{d.name}</b>
                    <span className="num">{formatDims(d.widthMm, d.heightMm, unit)}{sheet ? ' · ' + (counts.get(d.id) || 0) + ' here' : ''}</span>
                    <DpiBadge asset={a} design={d} builder={cfg.builder} unit={unit} />
                  </span>
                </button>
                <div className="os-q">
                  <Stepper small value={d.quantity} min={0} max={10000} onCommit={v => setQuantity([d.id], v)} ariaLabel={'Quantity of ' + d.name} />
                  {doc.sheets.length > 0 && <small className={short ? 'is-short' : ''}>{placedAll} of {d.quantity} placed</small>}
                </div>
              </li>
            );
          })}
        </ul>
        {!list.length && <p className="small muted sbox-note">{isManualDoc(doc)
          ? (doc.designs.length ? 'Your designs go onto the sheet as soon as they’re checked.' : 'Upload your designs: they go straight onto the sheet.')
          : (doc.designs.length ? 'Nothing on this sheet yet. Drag a design from Designs onto it.' : 'Upload your designs, then drag them onto the sheet.')}</p>}
        {doc.layoutStale && <button className="btn btn-soft btn-xs btn-block" style={{ marginTop: 10 }} onClick={() => void build('build')}><Icon name="build" />Rebuild with the new quantities</button>}
      </section>
      {doc.sheets.some(sh => sh.items.length > 0) && <section className="sbox"><ChecksSummary onMore={onReview} /></section>}
    </>
  );
}

/* --------------------------------------------------- the selected designs */
function SelectionProps({ items }: { items: Item[] }) {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const assets = useStore(s => s.assets);
  const unit = doc.settings.unit;
  const ids = items.map(i => i.id);
  const one = items.length === 1 ? items[0] : null;
  const designIds = [...new Set(items.map(i => i.designId))];
  const design = designIds.length === 1 ? doc.designs.find(d => d.id === designIds[0]) : undefined;
  const asset = design ? assets[design.assetId] : undefined;
  const [times, setTimes] = useState(10);
  const [fill, setFill] = useState(false);
  const [more, setMore] = useState(false);
  const maxL = cfg.pricing.transfers[doc.transfer].byLength.maxLengthMm;
  const locked = items.every(i => i.locked);
  const box = one ? itemBox(one) : null;
  const first = items[0];
  const isText = !!textSpecOf(design);
  const q = asset && asset.status === 'ready' && design ? effectiveDpi(asset, design.trim, first.widthMm, first.heightMm, cfg.builder) : null;

  // the editing commands are shared with the quick toolbar, the right-click menu and the phone edit bar
  const setSize = (w: number | null, h: number | null) => resizeSelection(ids, w, h);
  const setPos = (x: number | null, y: number | null) => { if (box) moveSelection(ids, x, y, box); };
  const rotate = (by: number | null, to?: number) => rotateSelection(ids, by, to);
  const reset = resetState(ids);
  const copiesOf = (designId: string) => doc.sheets.flatMap(s => s.items).filter(i => i.designId === designId);
  const sameAll = () => {
    if (!one || !design) return;
    updateItems('Same size for every copy', copiesOf(one.designId).map(i => i.id), (it, s) => {
      it.widthMm = one.widthMm; it.heightMm = one.heightMm;
      const p = clampInside(it, s, maxL); it.cx = p.cx; it.cy = p.cy;
    });
    toast('Every copy of ' + design.name + ' is now ' + formatDims(one.widthMm, one.heightMm, unit) + '.');
  };
  const bestSize = () => {
    if (!design || !asset || asset.status !== 'ready') return;
    autoSize([design.id]);
    const d = useStore.getState().doc.designs.find(x => x.id === design.id);
    if (d) setSize(d.widthMm, null);
  };
  const mixed = one && design && copiesOf(one.designId).some(x => Math.abs(x.widthMm - one.widthMm) > 0.05);
  // stretched: this copy's proportions no longer match the artwork's
  const artRatio = design && asset?.status === 'ready' ? aspectOf(design) : null;
  const stretch = one && artRatio ? (one.widthMm / one.heightMm) / artRatio : 1;
  const stretched = Math.abs(stretch - 1) > 0.01;
  const resetProportions = () => { if (artRatio) resetSelection(ids, { proportions: true }); };

  return (
    <section className="sbox sel">
      <div className="sbox-h">
        <span className="sbox-t">{one ? 'Selected design' : items.length + ' selected'}</span>
        <button className="ibtn ibtn-sm" aria-label="Deselect" data-tip="Deselect (Esc)" data-tip-pos="left" onClick={() => select([])}><Icon name="x" /></button>
      </div>
      {design && (
        <div className="sel-head">
          <span className="thumb">{asset?.thumbUrl && <img src={asset.thumbUrl} alt="" />}</span>
          <div className="sel-txt">
            <b title={design.name}>{design.name}</b>
            <DpiBadge asset={asset} design={{ trim: design.trim, widthMm: first.widthMm, heightMm: first.heightMm }} builder={cfg.builder} unit={unit} long />
          </div>
        </div>
      )}
      {q?.quality === 'low' && (
        <div className="callout callout-warn"><Icon name="alert" /><div>May look pixelated at this size.{asset?.kind === 'raster' && <> <button className="link" onClick={bestSize}>Use the best size</button></>}</div></div>
      )}

      <div className="sel-group">
        <div className="sel-lbl"><span className="label">Size</span>
          <LockToggle />
        </div>
        <div className="grid2">
          <NumberField label="Width" unit={unit} mmValue value={first.widthMm} min={3} max={5000} disabled={locked} onCommit={v => setSize(v, null)} small />
          <NumberField label="Height" unit={unit} mmValue value={first.heightMm} min={3} max={5000} disabled={locked} onCommit={v => setSize(null, v)} small />
        </div>
        {stretched && (
          <p className="stretch-note"><Icon name="info" /><span>{stretchText(stretch)}</span>{!locked && <button className="link small" onClick={resetProportions}>Reset proportions</button>}</p>
        )}
        {mixed && <button className="link small" style={{ marginTop: 6 }} onClick={sameAll}>Make every copy this size</button>}
      </div>

      <div className="sel-group">
        <span className="label">Rotate &amp; flip</span>
        <div className="sel-row">
          {one && <div style={{ width: 84 }}><NumberField unit="°" value={one.rotation} decimals={1} disabled={locked} ariaLabel="Rotation in degrees" onCommit={v => rotate(null, v)} small /></div>}
          <button className="ibtn" data-tip="Rotate left 90°" aria-label="Rotate left" disabled={locked} onClick={() => rotate(-90)}><Icon name="rotCcw" /></button>
          <button className="ibtn" data-tip="Rotate right 90°" aria-label="Rotate right" disabled={locked} onClick={() => rotate(90)}><Icon name="rotCw" /></button>
          <button className="ibtn" data-tip="Flip horizontal" aria-label="Flip horizontal" disabled={locked} onClick={() => flipSelection(ids, 'x')}><Icon name="flipH" /></button>
          <button className="ibtn" data-tip="Flip vertical" aria-label="Flip vertical" disabled={locked} onClick={() => flipSelection(ids, 'y')}><Icon name="flipV" /></button>
        </div>
        {!locked && items.some(isTurned) && <button className="link small sel-reset-rot" style={{ marginTop: 6 }} onClick={() => resetSelection(ids, { rotation: true })}>Reset rotation to 0°</button>}
      </div>

      <div className="sel-group">
        <span className="label">{items.length > 1 ? 'Align to each other' : 'Align on the sheet'}</span>
        <div className="sel-row aligns">
          {([['left', 'alignL', 'Align left'], ['hcenter', 'alignCH', 'Centre horizontally'], ['right', 'alignR', 'Align right'], ['top', 'alignT', 'Align top'], ['vcenter', 'alignCV', 'Centre vertically'], ['bottom', 'alignB', 'Align bottom']] as const).map(([k, ic, l]) => (
            <button key={k} className="ibtn" data-tip={l} aria-label={l} onClick={() => align(k)}><Icon name={ic} /></button>
          ))}
          {items.length >= 3 && <>
            <button className="ibtn" data-tip="Distribute horizontally" aria-label="Distribute horizontally" onClick={() => distribute('h')}><Icon name="distH" /></button>
            <button className="ibtn" data-tip="Distribute vertically" aria-label="Distribute vertically" onClick={() => distribute('v')}><Icon name="distV" /></button>
          </>}
        </div>
      </div>

      <div className="sel-acts">
        <button className="btn btn-soft btn-xs" onClick={() => void duplicateSelection(1)} data-tip="Ctrl/⌘ D"><Icon name="copy" />Duplicate</button>
        {one && <button className="btn btn-soft btn-xs" onClick={() => setFill(true)}><Icon name="fill" />Fill space</button>}
        {isText && design && <button className="btn btn-soft btn-xs" onClick={() => useStore.setState({ textEdit: design.id, panel: 'text', sideOpen: false })}><Icon name="type" />Edit text</button>}
        <button className="btn btn-danger btn-xs" onClick={deleteSelection} data-tip="Delete key"><Icon name="trash" />Delete</button>
      </div>

      <button className="more-t" aria-expanded={more} onClick={() => setMore(v => !v)}><Icon name="chevDown" className={'chev' + (more ? '' : ' is-shut')} />More options</button>
      {more && (
        <div className="more-b">
          {one && box && (
            <div className="sel-group">
              <span className="label">Position (from the top-left)</span>
              <div className="grid2">
                <NumberField label="X" unit={unit} mmValue value={box.x} min={0} disabled={locked} onCommit={v => setPos(v, null)} small />
                <NumberField label="Y" unit={unit} mmValue value={box.y} min={0} disabled={locked} onCommit={v => setPos(null, v)} small />
              </div>
            </div>
          )}
          <div className="sel-group">
            <span className="label">Many copies</span>
            <div className="sel-row">
              <Stepper small value={times} min={1} max={500} onCommit={setTimes} ariaLabel="How many copies" />
              <button className="btn btn-line btn-xs" onClick={() => void duplicateSelection(times)}>Duplicate × {times}</button>
            </div>
          </div>
          <div className="sel-group">
            <span className="label">Layer</span>
            <div className="sel-row">
              <button className="ibtn" data-tip="Bring to front" aria-label="Bring to front" onClick={() => layerSelection('front')}><Icon name="front" /></button>
              <button className="ibtn" data-tip="Bring forward" aria-label="Bring forward" onClick={() => layerSelection('forward')}><Icon name="forward" /></button>
              <button className="ibtn" data-tip="Send backward" aria-label="Send backward" onClick={() => layerSelection('backward')}><Icon name="backward" /></button>
              <button className="ibtn" data-tip="Send to back" aria-label="Send to back" onClick={() => layerSelection('back')}><Icon name="back" /></button>
            </div>
          </div>
          {(reset.proportions || reset.rotation) && (
            <div className="sel-group">
              <span className="label">Reset</span>
              <div className="sel-row">
                <button className="btn btn-line btn-xs" data-reset="both" onClick={() => resetSelection(ids, { proportions: true, rotation: true })}><Icon name="reset" />Reset design</button>
                <span className="tiny muted">{reset.proportions && reset.rotation ? 'Artwork proportions and 0°' : reset.proportions ? 'Artwork proportions' : 'Back to 0°'}</span>
              </div>
            </div>
          )}
          <div className="sel-row">
            <button className="btn btn-line btn-xs" aria-pressed={locked} onClick={() => toggleLockPosition(ids)}><Icon name={locked ? 'unlock' : 'lock'} />{locked ? 'Unlock' : 'Lock position'}</button>
            {design && <button className="btn btn-line btn-xs" onClick={() => void addCopy(design.id)}><Icon name="plus" />Add a copy where it fits</button>}
          </div>
        </div>
      )}
      {fill && <FillDialog onClose={() => setFill(false)} preset={one || undefined} />}
    </section>
  );
}

/* ------------------------------------------------------- pinned footer */
function SideFoot({ onReview }: { onReview: () => void }) {
  const est = useEstimate();
  const price = usePrice();
  const pf = usePreflight();
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const assets = useStore(s => s.assets);
  const ok = canOrder(pf, doc);
  const built = doc.sheets.some(s => s.items.length);
  const waiting = doc.designs.some(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing');
  return (
    <div className="side-f">
      <div className="sf-price">
        <div>
          <span className="label">{est?.built ? 'Your price' : 'Estimate'}</span>
          <b className="num">{est ? formatMoney(est.total) : formatMoney(0)}</b>
        </div>
        <div className="sf-meta">
          <span className="chip">{cfg.pricing.taxLabel} incl.</span>
          {price && price.saving > 0 && !doc.layoutStale && <span className="chip chip-orange">Bulk {Math.round((price.tier?.percentOff || 0) * 100)}% off</span>}
          {est && !est.built && est.lengthMm ? <small className="muted">about {(est.lengthMm / 1000).toFixed(1)} m</small> : null}
        </div>
      </div>
      {built || isManualDoc(doc)
        ? <button className="btn btn-primary btn-block" disabled={!built} onClick={onReview}>Review &amp; order<Icon name="arrowRight" className="ico-arrow" /></button>
        : <button className="btn btn-primary btn-block" disabled={!doc.designs.length || waiting} onClick={() => void build('build')}>{waiting ? <span className="spin" /> : <Icon name="build" />}{waiting ? 'Uploading…' : 'Build my gangsheet'}</button>}
      {built && !ok.ok && ok.why && <p className="tiny sf-why"><Icon name="alert" />{ok.why}</p>}
    </div>
  );
}

/* ------------------------------------------------------------ fill */
export function FillDialog({ onClose, preset }: { onClose: () => void; preset?: Item }) {
  const doc = useStore(s => s.doc);
  const assets = useStore(s => s.assets);
  const unit = doc.settings.unit;
  const ready = doc.designs.filter(d => assets[d.assetId]?.status === 'ready');
  const [designId, setDesignId] = useState(preset?.designId || ready[0]?.id || '');
  const d = doc.designs.find(x => x.id === designId) as Design | undefined;
  const w = preset && preset.designId === designId ? preset.widthMm : d?.widthMm || 0;
  const h = preset && preset.designId === designId ? preset.heightMm : d?.heightMm || 0;
  const [count, setCount] = useState<number | null>(null);
  const [n, setN] = useState(1);
  const reqRef = useRef(0);
  useEffect(() => {
    if (!designId) return;
    const r = ++reqRef.current;
    setCount(null);
    void fillWith(designId, w, h).then(c => { if (r === reqRef.current) { setCount(c); setN(c); } });
  }, [designId, w, h]);
  return (
    <Dialog title="Fill empty space" onClose={onClose}>
      <span className="kicker">Fill empty space</span>
      <h2>Use the film you’re paying for</h2>
      <p className="muted small">We’ll fit as many copies as we can into the gaps on sheet {useStore.getState().activeSheet + 1}, keeping {doc.settings.gapMm} mm between everything.</p>
      <label className="field" style={{ marginTop: 14 }}>
        <span className="label">Design</span>
        <select className="in" value={designId} onChange={e => setDesignId(e.target.value)}>
          {ready.map(x => <option key={x.id} value={x.id}>{x.name} · {formatDims(x.widthMm, x.heightMm, unit)}</option>)}
        </select>
      </label>
      <div className="callout callout-info" style={{ marginTop: 14 }}>
        {count === null ? <><span className="spin" />Finding space…</> : count === 0 ? <><Icon name="info" />No more copies fit at {formatDims(w, h, unit)}. Try a smaller size.</> : <><Icon name="fill" /><b>{count} additional {count === 1 ? 'copy' : 'copies'} can fit.</b></>}
      </div>
      {!!count && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 14 }}>
          <Stepper label="Choose quantity" value={n} min={1} max={count} onCommit={setN} />
          <button className="btn btn-line btn-sm" onClick={async () => { await fillWith(designId, w, h, n); onClose(); }}>Add {n}</button>
        </div>
      )}
      <div className="dialog-actions">
        <button className="btn btn-line btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={!count} onClick={async () => { await fillWith(designId, w, h, count || 0); onClose(); }}>Add all {count || ''}</button>
      </div>
    </Dialog>
  );
}

/** Lock proportions: one switch for the canvas handles, these size fields and the design sizes. */
export function LockToggle({ compact }: { compact?: boolean }) {
  const on = useStore(s => s.lockAspect);
  return (
    <button type="button" className={'lockbtn' + (compact ? ' is-compact' : '')} aria-pressed={on} onClick={() => setLockAspect(!on)}
      data-tip={on ? 'Width and height change together. Click to stretch freely (or hold Shift while dragging a handle)' : 'Width and height change separately. Click to keep proportions'} data-tip-pos="left">
      <Icon name={on ? 'lock' : 'unlock'} /><span>{on ? 'Lock proportions' : 'Proportions unlocked'}</span>
    </button>
  );
}
